import { useQuery } from "@tanstack/react-query";
import {
  ProductAvailabilityClient,
  type ProductAvailabilityResponse,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import { EcommerceQueryKeys } from "./ecommerceQueryKeys";
import { standardsManagementAdminRoles } from "@features/ecommerce/hooks/standardsAuthorization";

export interface StandardsProductAvailabilityResult {
  data: ProductAvailabilityResponse | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Fetches AX-warehouse stock availability for the Quick View drawer's Stock / Inventory tab.
 * Gated on the same roles and permissions used by the product details endpoint.
 *
 * `orgUnitId` is the target org unit/storefront being viewed and must be supplied by the
 * caller (mirroring useGetCatalogRules/useGetProductDetailDrawer), not derived internally
 * from the viewer's own current-org claim: an Admin or storefront manager browsing a
 * different storefront could have a current org unit that differs from the storefront
 * being viewed.
 */
export default function useStandardsProductAvailability(
  productDescriptor: string | null | undefined,
  orgUnitId: number | null | undefined,
  enabled: boolean,
): StandardsProductAvailabilityResult {
  const api = useApi(ProductAvailabilityClient);
  const { hasPermission, hasRoleIn, hasResourcePermission } = useClaims();
  const canViewProductDetails =
    hasRoleIn(...standardsManagementAdminRoles) ||
    (orgUnitId != null &&
      hasResourcePermission(ShiOnePermission.storefrontManage, orgUnitId)) ||
    hasPermission(ShiOnePermission.featuredProductsView) ||
    hasPermission(ShiOnePermission.featuredProductsAdd);
  const fetchProductAvailability =
    async (): Promise<ProductAvailabilityResponse | null> => {
      if (!productDescriptor || orgUnitId == null) return null;
      return await api.getProductAvailability(orgUnitId, productDescriptor);
    };

  const query = useQuery({
    queryKey: [
      EcommerceQueryKeys.ProductAvailability,
      { productDescriptor, orgUnitId },
    ],
    queryFn: fetchProductAvailability,
    enabled:
      enabled &&
      canViewProductDetails &&
      orgUnitId != null &&
      !!productDescriptor,
    retry: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isInitialLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
